
import React, { useState, useEffect, useRef } from 'react'
import { Search, User, X } from 'lucide-react'
import { userService, UserSearchResult } from '@/services/userService'
import { Body } from '@meiuca/ui-components'

interface UserSearchInputProps {
  onUserSelect: (user: UserSearchResult) => void
  placeholder?: string
  className?: string
  disabled?: boolean
}

export function UserSearchInput({ onUserSelect, placeholder = "Buscar usuário...", className = "", disabled = false }: UserSearchInputProps) {
  const [query, setQuery] = useState('')
  const [users, setUsers] = useState<UserSearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  
  const searchRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    if (disabled || query.trim().length < 2) {
      setUsers([])
      setShowResults(false)
      return
    }

    debounceRef.current = setTimeout(async () => {
      try {
        setIsSearching(true)
        const searchResults = await userService.searchUsers(query)
        setUsers(searchResults)
        setShowResults(true)
        setSelectedIndex(-1)
      } catch (error) {
        console.error('Search error:', error)
        setUsers([])
        setShowResults(false)
      } finally {
        setIsSearching(false)
      }
    }, 300)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [query])

  // Click outside to close
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false)
        setSelectedIndex(-1)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showResults || users.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev => (prev < users.length - 1 ? prev + 1 : 0))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : users.length - 1))
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0 && selectedIndex < users.length) {
          handleUserSelect(users[selectedIndex])
        }
        break
      case 'Escape':
        setShowResults(false)
        setSelectedIndex(-1)
        inputRef.current?.blur()
        break
    }
  }

  const handleUserSelect = (user: UserSearchResult) => {
    onUserSelect(user)
    setQuery('')
    setUsers([])
    setShowResults(false)
    setSelectedIndex(-1)
  }

  const clearSearch = () => {
    setQuery('')
    setUsers([])
    setShowResults(false)
    setSelectedIndex(-1)
    inputRef.current?.focus()
  }

  return (
    <div ref={searchRef} className={`relative ${className}`}>
      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-4 w-4 text-text-tertiary" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => !disabled && setQuery(e.target.value)}
          onKeyDown={!disabled ? handleKeyDown : undefined}
          onFocus={() => {
            if (!disabled && query.trim().length >= 2 && users.length > 0) {
              setShowResults(true)
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full pl-10 pr-10 py-3 border border-border-primary rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent bg-background-white text-text-primary placeholder-text-tertiary disabled:bg-background-secondary disabled:cursor-not-allowed disabled:opacity-50"
        />
        {query && !disabled && (
          <button
            onClick={clearSearch}
            className="absolute inset-y-0 right-0 pr-3 flex items-center hover:text-text-primary transition-colors"
          >
            <X className="h-4 w-4 text-text-tertiary" />
          </button>
        )}
      </div>

      {/* Search Results */}
      {showResults && !disabled && (
        <div className="absolute z-50 w-full mt-2 bg-background-white border border-border-primary rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {isSearching && (
            <div className="p-4 text-center">
              <div className="inline-flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-brand-primary border-t-transparent"></div>
                <Body className="text-text-secondary">Buscando usuários...</Body>
              </div>
            </div>
          )}

          {!isSearching && users.length === 0 && (
            <div className="p-4 text-center">
              <Body className="text-text-secondary">Nenhum usuário encontrado</Body>
            </div>
          )}

          {!isSearching && users.length > 0 && (
            <div className="py-1">
              {users.map((user, index) => (
                <button
                  key={user.id}
                  onClick={() => handleUserSelect(user)}
                  className={`w-full px-4 py-3 text-left hover:bg-background-secondary transition-colors flex items-center space-x-3 ${
                    index === selectedIndex ? 'bg-background-secondary' : ''
                  }`}
                >
                  <div className="flex-shrink-0">
                    {user.avatar ? (
                      <img
                        src={user.avatar}
                        alt={user.name}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-brand-secondary rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <Body className="font-medium text-text-primary truncate">
                      {user.name}
                    </Body>
                    {user.nickname && (
                      <Body className="text-sm text-text-secondary truncate">
                        @{user.nickname}
                      </Body>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
